// A series should not appear on the site until it has at least one episode,
// whoever (creator or admin) uploaded it. The listing endpoints (sections,
// /home, /series, search, genre, creator, my-list, related content) don't say
// how many episodes a series has, so each listed series is checked against
// its detail payload (client/v1/content/:slug) and dropped when no season has
// an episode. Results are cached per slug for a minute so a series shared by
// several rails/pages is only looked up once.

const CACHE_TTL_MS = 60 * 1000;
const cache = new Map(); // slug -> { at, promise<boolean> }

const isSeries = (item) =>
  item &&
  typeof item === "object" &&
  item.slug &&
  /^series$/i.test(item.content_type || item.type || "");

export const seriesDetailHasEpisodes = (detail) =>
  (detail?.seasons || []).some((season) => season?.episodes?.length > 0);

const seriesHasEpisodes = (slug, fetchDetail) => {
  const hit = cache.get(slug);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

  const promise = fetchDetail(slug)
    .then((res) => {
      if (res?.data?.status !== "success") return true; // unknown -> keep
      return seriesDetailHasEpisodes(res.data.data);
    })
    .catch(() => {
      cache.delete(slug); // don't remember failures
      return true; // network/API error -> keep rather than hide real content
    });

  cache.set(slug, { at: Date.now(), promise });
  return promise;
};

// Only items inside arrays are list entries; a detail payload's own root
// object is never collected or removed.
const collectSeriesSlugs = (node, slugs, inList = false) => {
  if (Array.isArray(node)) {
    node.forEach((child) => collectSeriesSlugs(child, slugs, true));
  } else if (node && typeof node === "object") {
    if (inList && isSeries(node)) slugs.add(node.slug);
    Object.values(node).forEach((child) => collectSeriesSlugs(child, slugs));
  }
};

const prune = (node, emptySlugs) => {
  if (Array.isArray(node)) {
    return node
      .filter((child) => !(isSeries(child) && emptySlugs.has(child.slug)))
      .map((child) => prune(child, emptySlugs));
  }
  if (node && typeof node === "object") {
    const out = {};
    Object.keys(node).forEach((key) => {
      out[key] = prune(node[key], emptySlugs);
    });
    return out;
  }
  return node;
};

/**
 * Returns a copy of an API response with every episode-less series removed
 * from any list inside `response.data.data`.
 */
export const hideEmptySeries = async (response, fetchDetail) => {
  const payload = response?.data?.data;
  if (!payload) return response;

  const slugs = new Set();
  collectSeriesSlugs(payload, slugs);
  if (slugs.size === 0) return response;

  const emptySlugs = new Set();
  await Promise.all(
    [...slugs].map(async (slug) => {
      if (!(await seriesHasEpisodes(slug, fetchDetail))) emptySlugs.add(slug);
    })
  );
  if (emptySlugs.size === 0) return response;

  return {
    ...response,
    data: { ...response.data, data: prune(payload, emptySlugs) },
  };
};
